import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useAuthStore } from "../stores/authStore";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import "./CSS/Shopcategory.css";
import { Filter, X, ChevronDown, ChevronUp, SlidersHorizontal, Grid3X3, List, Star, Loader2 } from "lucide-react";
import { apiService } from "../services/api";
import { useBoutiqueProducts } from "../hooks/useBoutiques";
import Item from "../componnents/Item/item";
import { ErrorMessage, LoadingSpinner, ProductSkeleton, EmptyState } from "../componnents/UI";
import {
  filterListableProducts,
  getProductImageUrl,
  getProductId,
  getProductName,
  getProductPrices,
  isProductInStock,
  isOwnSellerId,
} from "../utils/helpers";
import { translateProducts } from "../utils/translateData";
import BoutiqueBadges from "../componnents/BoutiqueBadges/BoutiqueBadges";
import { isPromotionCurrentlyActive } from "../utils/sellerPromotion.js";

const DEFAULT_BANNER = "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&h=400&fit=crop";

export default function BoutiqueDetail() {
  const { t } = useTranslation(["search", "common", "conversations"]);
  const { boutiqueId } = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);

  const { data: sellerData, isLoading: isLoadingSeller, error: sellerError } = useQuery({
    queryKey: ["seller", boutiqueId],
    queryFn: () => apiService.getSeller(boutiqueId),
    enabled: !!boutiqueId,
  });

  const boutique = sellerData?.data ?? sellerData;

  const [currentPage, setCurrentPage] = useState(1);
  const [allProducts, setAllProducts] = useState([]);

  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minRating, setMinRating] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [inStock, setInStock] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [expandedSections, setExpandedSections] = useState({ price: true, rating: false });

  const productParams = useMemo(() => ({
    page: currentPage,
    limit: 20,
    ...(minPrice && { min_price: minPrice }),
    ...(maxPrice && { max_price: maxPrice }),
    ...(inStock && { in_stock: "1" }),
    ...(categoryFilter.trim() && { category: categoryFilter.trim() }),
    ...(sortBy && sortBy !== "newest" && { sort: sortBy }),
  }), [currentPage, minPrice, maxPrice, inStock, categoryFilter, sortBy]);

  const { data: productsData, isLoading: isLoadingProducts, error: productsError } = useBoutiqueProducts(boutiqueId, productParams);

  const total = productsData?.total ?? 0;
  const firstProductOwner = productsData?.products?.[0]?.owner;
  const displayName = (boutique?.name === "Boutique" && firstProductOwner?.name)
    ? firstProductOwner.name
    : (boutique?.name ?? "Boutique");

  const boutiqueBadges = boutique
    ? {
        ...boutique,
        isPromotionActive: isPromotionCurrentlyActive(boutique),
      }
    : null;

  useEffect(() => {
    if (!productsData) return;
    const list = translateProducts(productsData.products ?? []);
    if (currentPage === 1) {
      setAllProducts(list);
    } else if (!isLoadingProducts) {
      setAllProducts((prev) => [...prev, ...list]);
    }
  }, [productsData, currentPage, isLoadingProducts]);

  const filteredProducts = useMemo(() => {
    let result = filterListableProducts(allProducts);
    if (minRating) {
      const r = Number(minRating);
      result = result.filter((item) => (item.averageRating || item.rating || 0) >= r);
    }
    return result;
  }, [allProducts, minRating]);

  const hasMore = productsData && currentPage * 20 < total;
  const hasActiveFilters = minPrice || maxPrice || minRating || inStock || categoryFilter.trim() || sortBy !== "newest";

  const clearFilters = () => {
    setMinPrice("");
    setMaxPrice("");
    setMinRating("");
    setSortBy("newest");
    setInStock(false);
    setCategoryFilter("");
    setCurrentPage(1);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const loadMore = () => setCurrentPage((p) => p + 1);
  const isLoadingMore = currentPage > 1 && isLoadingProducts;

  const pricePresets = [
    { label: t("pricePresets.under25", { ns: "search" }), min: "", max: "25" },
    { label: t("pricePresets.25to50", { ns: "search" }), min: "25", max: "50" },
    { label: t("pricePresets.50to100", { ns: "search" }), min: "50", max: "100" },
    { label: t("pricePresets.100to200", { ns: "search" }), min: "100", max: "200" },
    { label: t("pricePresets.over200", { ns: "search" }), min: "200", max: "" },
  ];

  const ratingOptions = [
    { value: "4", label: t("ratingOptions.4up", { ns: "search" }) },
    { value: "3", label: t("ratingOptions.3up", { ns: "search" }) },
    { value: "2", label: t("ratingOptions.2up", { ns: "search" }) },
  ];

  const sortOptions = [
    { value: "newest", label: t("sortOptions.newest", { ns: "search" }) },
    { value: "best_selling", label: t("sortOptions.popular", { ns: "search" }) },
    { value: "price_asc", label: t("sortOptions.price_asc", { ns: "search" }) },
    { value: "price_desc", label: t("sortOptions.price_desc", { ns: "search" }) },
  ];

  if (isLoadingSeller) {
    return (
      <div className="shop-category">
        <div className="category-content" style={{ justifyContent: "center", minHeight: 300 }}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (sellerError || !boutique) {
    return (
      <div className="shop-category">
        <div className="category-content" style={{ padding: 40 }}>
          <ErrorMessage message={t("boutiqueNotFound", { defaultValue: "Boutique not found.", ns: "common" })} />
          <Link to="/boutiques" style={{ marginTop: 16, display: "inline-block", color: "var(--color-primary)" }}>
            {t("backToBoutiques", { defaultValue: "Back to Boutiques", ns: "common" })}
          </Link>
        </div>
      </div>
    );
  }

  const bannerUrl = boutique.logo || DEFAULT_BANNER;
  const sellerId = boutique.id || boutique._id || boutiqueId;
  const isOwnBoutique = isOwnSellerId(sellerId, user);

  const handleMessageStore = () => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: `/boutiques/${boutiqueId}` } });
      return;
    }
    if (isOwnBoutique) return;
    navigate("/conversations", {
      state: { sellerId, storeName: displayName },
    });
  };

  return (
    <div className="shop-category">
      <div className="category-banner-wrapper">
        <img src={bannerUrl} alt={displayName} className="Category-Banner" />
        <div className="category-banner-overlay">
          <h1>{displayName}</h1>
          {boutiqueBadges && <BoutiqueBadges boutique={boutiqueBadges} className="boutique-detail-badges" />}
          {(boutique.noOfRating != null || boutique.rating != null) && (
            <p>
              <Star size={18} fill="currentColor" style={{ verticalAlign: "middle", marginRight: 6 }} />
              {Number(boutique.noOfRating ?? boutique.rating ?? 0).toFixed(1)} {t("rating", { ns: "search" })}
            </p>
          )}
          {!isOwnBoutique && (
            <button
              type="button"
              className="boutique-message-btn"
              onClick={handleMessageStore}
            >
              <MessageCircle size={18} />
              {t("messageStore", { defaultValue: "Message", ns: "conversations" })}
            </button>
          )}
        </div>
      </div>

      <div className="category-content">
        <aside className={`category-filters ${showFilters ? "open" : ""}`}>
          <div className="filters-header">
            <h3>
              <Filter size={18} />
              {t("filters", { ns: "search" })}
            </h3>
            {hasActiveFilters && (
              <button type="button" className="clear-all-btn" onClick={clearFilters}>
                {t("clearAll", { ns: "search" })}
              </button>
            )}
            <button type="button" className="close-filters" onClick={() => setShowFilters(false)}>
              <X size={24} />
            </button>
          </div>

          <div className="filter-section">
            <button type="button" className="filter-section-header" onClick={() => toggleSection("price")}>
              <span>{t("priceRange", { ns: "search" })}</span>
              {expandedSections.price ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.price && (
              <div className="filter-section-content">
                <div className="price-inputs">
                  <div className="price-input-group">
                    <label>{t("min", { ns: "search" })}</label>
                    <div className="price-input-wrapper">
                      <span className="currency">$</span>
                      <input
                        type="number"
                        value={minPrice}
                        onChange={(e) => { setMinPrice(e.target.value); setCurrentPage(1); }}
                        placeholder="0"
                        min="0"
                      />
                    </div>
                  </div>
                  <span className="price-separator">–</span>
                  <div className="price-input-group">
                    <label>{t("max", { ns: "search" })}</label>
                    <div className="price-input-wrapper">
                      <span className="currency">$</span>
                      <input
                        type="number"
                        value={maxPrice}
                        onChange={(e) => { setMaxPrice(e.target.value); setCurrentPage(1); }}
                        placeholder={t("any", { ns: "search" })}
                        min="0"
                      />
                    </div>
                  </div>
                </div>
                <div className="price-presets">
                  {pricePresets.map((preset, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`price-preset ${minPrice === preset.min && maxPrice === preset.max ? "active" : ""}`}
                      onClick={() => {
                        setMinPrice(preset.min);
                        setMaxPrice(preset.max);
                        setCurrentPage(1);
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="filter-section">
            <button type="button" className="filter-section-header" onClick={() => toggleSection("rating")}>
              <span>{t("customerRating", { ns: "search" })}</span>
              {expandedSections.rating ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
            {expandedSections.rating && (
              <div className="filter-section-content">
                {ratingOptions.map((option) => (
                  <label
                    key={option.value}
                    className={`filter-option rating-option ${minRating === option.value ? "active" : ""}`}
                  >
                    <input
                      type="radio"
                      name="boutique-rating"
                      checked={minRating === option.value}
                      onChange={() => setMinRating(minRating === option.value ? "" : option.value)}
                    />
                    <div className="rating-stars">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          fill={i < Number(option.value) ? "#fbbf24" : "none"}
                          stroke={i < Number(option.value) ? "#fbbf24" : "#d1d5db"}
                        />
                      ))}
                    </div>
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="filter-section">
            <label className="filter-option" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input
                type="checkbox"
                checked={inStock}
                onChange={(e) => { setInStock(e.target.checked); setCurrentPage(1); }}
              />
              <span>{t("inStockOnly", { defaultValue: "In stock only", ns: "common" })}</span>
            </label>
          </div>

          <div className="filter-section">
            <label style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
              {t("category", { ns: "search" })}
            </label>
            <input
              type="text"
              value={categoryFilter}
              onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
              placeholder={t("any", { ns: "search" })}
              className="price-input-wrapper"
              style={{ width: "100%", padding: "10px 12px" }}
            />
          </div>

          <button type="button" className="apply-filters-btn" onClick={() => setShowFilters(false)}>
            {t("showResults", { count: filteredProducts.length, ns: "search" })}
          </button>
        </aside>

        <main className="category-products-main">
          <div className="category-results-header">
            <div className="results-controls">
              <button
                type="button"
                className={`filter-toggle ${showFilters ? "active" : ""}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <SlidersHorizontal size={18} />
                <span>{t("filters", { ns: "search" })}</span>
                {hasActiveFilters && <span className="filter-badge" />}
              </button>
              <div className="view-toggle">
                <button
                  type="button"
                  className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                  onClick={() => setViewMode("grid")}
                  title={t("gridView", { ns: "search" })}
                >
                  <Grid3X3 size={18} />
                </button>
                <button
                  type="button"
                  className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                  onClick={() => setViewMode("list")}
                  title={t("listView", { ns: "search" })}
                >
                  <List size={18} />
                </button>
              </div>
              <div className="sort-wrapper">
                <label>{t("sort", { ns: "search" })}:</label>
                <select
                  value={sortBy}
                  onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
                  className="sort-select"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {hasActiveFilters && (
            <div className="active-filters">
              {(minPrice || maxPrice) && (
                <span className="filter-tag">
                  <span className="tag-label">{t("price", { ns: "search" })}:</span>
                  ${minPrice || "0"} – ${maxPrice || t("any", { ns: "search" })}
                  <button type="button" onClick={() => { setMinPrice(""); setMaxPrice(""); setCurrentPage(1); }}>
                    <X size={14} />
                  </button>
                </span>
              )}
              {minRating && (
                <span className="filter-tag">
                  <span className="tag-label">{t("rating", { ns: "search" })}:</span>
                  {minRating}★ & Up
                  <button type="button" onClick={() => setMinRating("")}>
                    <X size={14} />
                  </button>
                </span>
              )}
              {inStock && (
                <span className="filter-tag">
                  {t("inStockOnly", { ns: "common" })}
                  <button type="button" onClick={() => { setInStock(false); setCurrentPage(1); }}>
                    <X size={14} />
                  </button>
                </span>
              )}
            </div>
          )}

          {isLoadingProducts && currentPage === 1 ? (
            <div className="products-grid">
              <ProductSkeleton count={8} />
            </div>
          ) : productsError ? (
            <div className="category-error">
              <p>{t("failedToLoad", { ns: "search" })}</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <EmptyState
              icon="package"
              title={t("noResults", { ns: "search" })}
              description={hasActiveFilters ? t("noResultsDescription", { ns: "search" }) : t("noProductsInBoutique", { defaultValue: "No products in this boutique yet.", ns: "common" })}
              actionText={hasActiveFilters ? t("clearFilters", { ns: "search" }) : undefined}
              onAction={hasActiveFilters ? clearFilters : undefined}
            />
          ) : (
            <>
              <div className={viewMode === "grid" ? "products-grid" : "products-list"}>
                {filteredProducts.map((item, i) => {
                  const { price, comparePrice, fromPrice } = getProductPrices(item);
                  return (
                    <Item
                      key={getProductId(item) || i}
                      id={getProductId(item)}
                      image={getProductImageUrl(item)}
                      name={getProductName(item)}
                      newPrice={price}
                      oldPrice={comparePrice}
                      fromPrice={fromPrice}
                      sold={item.sold || item.salesCount || 0}
                      rating={item.averageRating || item.rating || 0}
                      shipping={item.freeShipping ? t("freeShipping", { ns: "common" }) : t("standardShipping", { ns: "common" })}
                      inStock={isProductInStock(item)}
                    />
                  );
                })}
              </div>

              {hasMore && (
                <div className="load-more-container" style={{ textAlign: "center", marginTop: "2rem", padding: "1rem" }}>
                  <button
                    type="button"
                    className="load-more-btn"
                    onClick={loadMore}
                    disabled={isLoadingMore}
                    style={{
                      padding: "0.75rem 2rem",
                      fontSize: "1rem",
                      fontWeight: 500,
                      color: "#fff",
                      backgroundColor: "#2563eb",
                      border: "none",
                      borderRadius: "0.5rem",
                      cursor: isLoadingMore ? "not-allowed" : "pointer",
                      opacity: isLoadingMore ? 0.7 : 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {isLoadingMore ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        {t("loadingMore", { ns: "common" })}
                      </>
                    ) : (
                      t("loadMore", { ns: "common" })
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
